import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Download, UploadCloud, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function BulkUploadPage({ kind = "employees" }) {
  // kind: "employees" | "students"
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const isEmp = kind === "employees";
  const title = isEmp ? "Bulk Upload Employees" : "Bulk Upload Students";
  const subtitle = isEmp ? "HR workspace" : "Institution workspace";

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/records/${kind}/template.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ethiccred_${kind}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template.");
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error("Please select a CSV file first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/records/${kind}/bulk`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} added · ${data.skipped} skipped`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceLayout subtitle={subtitle} title={title}>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="rounded-sm border-ec-line">
          <CardContent className="p-6">
            <div className="eyebrow">Step 1</div>
            <h3 className="font-display text-xl font-bold mt-1">Download the template</h3>
            <p className="text-sm text-ec-ink-soft mt-2">
              Download the official CSV template. Required columns include name, personal email, phone, {isEmp ? "employee" : "student"} ID, and {isEmp ? "designation, department, joining date" : "course, batch, admission year"}. PAN is optional, encrypted at rest, and only displayed in masked form.
            </p>
            <Button onClick={downloadTemplate} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2 mt-5" data-testid="bulk-download-template">
              <Download className="w-4 h-4" /> Download CSV template
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-ec-line">
          <CardContent className="p-6">
            <div className="eyebrow">Step 2</div>
            <h3 className="font-display text-xl font-bold mt-1">Upload your file</h3>
            <p className="text-sm text-ec-ink-soft mt-2">
              We will create unclaimed records for each row. If a row&apos;s email already matches an EthicCred user, we link automatically. Duplicate {isEmp ? "Employee" : "Student"} IDs are skipped.
            </p>
            <label className="block border-2 border-dashed border-ec-line bg-ec-mute hover:border-ec-emerald hover:bg-ec-emerald/5 transition-colors p-8 mt-5 text-center cursor-pointer rounded-sm" data-testid="bulk-dropzone">
              <FileSpreadsheet className="w-8 h-8 mx-auto text-ec-emerald" />
              <div className="mt-2 text-sm font-semibold">{file ? file.name : "Click or drop CSV here"}</div>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-file-input"
              />
            </label>
            <Button onClick={upload} disabled={loading || !file} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 gap-2 mt-5 w-full" data-testid="bulk-upload-button">
              <UploadCloud className="w-4 h-4" /> {loading ? "Uploading…" : "Upload & Process"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {result && (
        <Card className="rounded-sm border-ec-line mt-6" data-testid="bulk-result">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-6 mb-4">
              <Stat icon={CheckCircle2} color="text-ec-emerald" label="Created" value={result.created} testid="bulk-created" />
              <Stat icon={AlertTriangle} color="text-amber-600" label="Skipped" value={result.skipped} testid="bulk-skipped" />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="border-t border-ec-line pt-4">
                <div className="eyebrow mb-2">Skipped rows</div>
                <ul className="text-xs space-y-1.5 max-h-72 overflow-y-auto font-mono-plex">
                  {result.errors.map((e, idx) => (
                    <li key={idx} className="text-ec-ink-soft">
                      Row {e.row}: <span className="text-ec-error">{e.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-5 flex gap-3">
              <Button variant="outline" className="rounded-sm" onClick={() => nav(`/workspace/${kind}`)} data-testid="bulk-view-directory">
                View {isEmp ? "Employee" : "Student"} directory
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </WorkspaceLayout>
  );
}

function Stat({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <Icon className={`w-6 h-6 ${color}`} />
      <div>
        <div className="font-display text-2xl font-black">{value}</div>
        <div className="eyebrow">{label}</div>
      </div>
    </div>
  );
}
